import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  FaWhatsapp, 
  FaUsers,
  FaChartLine,
  FaCheckCircle,
  FaClock,
  FaRocket,
  FaChevronRight,
  FaTools, 
  FaEnvelope,
  FaPhone,
  FaStar,
  FaCalendarCheck,
  FaHeadset,
  FaLightbulb,
  FaArrowRight,
  FaArrowUp
} from 'react-icons/fa';
import { readySystems } from '../data/readySystems';

const ReadySystems = () => {
  const getWhatsAppMessage = (system) => {
    return encodeURIComponent(
      `Hello Shyee Codes! I'm interested in the ${system.name} system. \n\n` +
      `Category: ${system.category}\n` +
      `Starting Price: ${system.displayPrice}\n` +
      `Implementation Time: ${system.implementationTime}\n\n` +
      `Please share more details and the next steps.`
    );
  };

  const renderFeature = (feature, index) => {
    if (typeof feature === 'string') {
      return (
        <div key={index} className="flex items-center gap-3">
          <FaCheckCircle className="text-[#fdaa01] flex-shrink-0" />
          <span className="text-sm text-gray-700">{feature}</span>
        </div>
      );
    }
    
    if (feature && typeof feature === 'object') {
      return (
        <div key={index} className="flex items-center gap-3">
          <span className="text-[#fdaa01] flex-shrink-0">
            {feature.icon || <FaCheckCircle />}
          </span>
          <span className="text-sm text-gray-700">{feature.text || feature}</span>
        </div>
      );
    }
    
    return (
      <div key={index} className="flex items-center gap-3">
        <FaCheckCircle className="text-[#fdaa01] flex-shrink-0" />
        <span className="text-sm text-gray-700">{String(feature)}</span>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#000324] via-[#010536] to-[#020a5c]">
      {/* Hero Section */}
      <section className="relative py-20 lg:py-28 overflow-hidden">
        {/* Background decorative elements */}
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_50%,rgba(253,170,1,0.1)_0%,transparent_50%),radial-gradient(circle_at_80%_20%,rgba(99,102,241,0.1)_0%,transparent_50%)]" />
        <div className="absolute top-20 left-10 w-64 h-64 bg-[#fdaa01]/10 rounded-full blur-3xl animate-float" />
        <div className="absolute bottom-20 right-10 w-72 h-72 bg-blue-500/10 rounded-full blur-3xl animate-float [animation-delay:5s]" />
        
        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <motion.span
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="inline-flex items-center gap-2 bg-[#fdaa01]/10 border border-[#fdaa01]/30 text-[#fdaa01] px-6 py-3 rounded-full text-sm font-semibold mb-6"
            >
              <FaRocket className="animate-pulse" />
              Ready-to-Launch Solutions
            </motion.span>
            
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white mb-6">
              Ready-to-Launch{' '}
              <span className="bg-gradient-to-r from-[#fdaa01] to-[#ffc042] bg-clip-text text-transparent">
                Solutions
              </span>
            </h1>
            
            <p className="text-lg sm:text-xl text-white/80 max-w-2xl mx-auto leading-relaxed mb-8">
              Industry-specific systems that have transformed businesses across Kenya. 
              Customizable, scalable, and ready to deploy in weeks.
            </p>
            
            <div className="w-24 h-1 bg-gradient-to-r from-[#fdaa01] to-transparent mx-auto" />
          </motion.div>
        </div>
      </section>

      {/* Systems Grid */}
      <section className="py-16 lg:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
            {readySystems.map((system, idx) => (
              <motion.div
                key={system.id}
                className="group bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-500 border border-gray-100 hover:border-[#fdaa01]/50 overflow-hidden flex flex-col"
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ delay: idx * 0.1 }}
                whileHover={{ y: -10 }}
              >
                {/* Top gradient bar */}
                <div 
                  className="h-1 w-full opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                  style={{ background: `linear-gradient(90deg, ${system.color}, ${system.color}cc)` }}
                />
                
                <div className="p-6 sm:p-8 flex-1 flex flex-col">
                  {/* System Header */}
                  <div className="flex items-start gap-4 mb-6">
                    <div 
                      className="w-16 h-16 rounded-2xl flex items-center justify-center flex-shrink-0 transform group-hover:scale-110 group-hover:rotate-6 transition-all duration-500"
                      style={{ backgroundColor: `${system.color}15` }}
                    >
                      <span style={{ color: system.color }} className="text-2xl">
                        {system.icon}
                      </span>
                    </div>
                    <div className="flex-1">
                      <h3 className="text-lg font-bold text-gray-900 mb-2 group-hover:text-[#010536] transition-colors duration-300">
                        {system.name}
                      </h3>
                      <span 
                        className="inline-block text-xs font-semibold px-3 py-1 rounded-full"
                        style={{ 
                          backgroundColor: `${system.color}10`,
                          color: system.color
                        }}
                      >
                        {system.category}
                      </span>
                    </div>
                  </div>

                  {/* Duration Info */}
                  <div className="flex items-center gap-2 text-sm text-gray-500 mb-4 bg-gray-50 rounded-lg px-3 py-2">
                    <FaClock className="text-[#fdaa01] flex-shrink-0" />
                    <span>
                      Duration to Launch: {system.duration || system.implementationTime}
                    </span>
                  </div>

                  {/* Description */}
                  <p className="text-gray-600 leading-relaxed mb-6 line-clamp-3">
                    {system.description}
                  </p>

                  {/* Features */}
                  <div className="mb-6">
                    <h4 className="flex items-center gap-2 text-sm font-bold text-gray-900 mb-3">
                      <FaTools className="text-[#fdaa01]" />
                      Key Features
                    </h4>
                    <div className="space-y-2">
                      {system.features && system.features.slice(0, 3).map((feature, fIdx) => 
                        renderFeature(feature, fIdx)
                      )}
                    </div>
                  </div>

                  {/* Results */}
                  {system.results && system.results.length > 0 && (
                    <div className="mb-6">
                      <h4 className="flex items-center gap-2 text-sm font-bold text-gray-900 mb-3">
                        <FaChartLine className="text-[#fdaa01]" />
                        Measurable Results
                      </h4>
                      <div className="grid grid-cols-3 gap-3">
                        {system.results.map((result, rIdx) => (
                          <div 
                            key={rIdx} 
                            className="text-center bg-gray-50 rounded-xl p-3 hover:bg-gray-100 transition-colors duration-300"
                          >
                            <div className="text-lg font-extrabold text-[#010536]">
                              {result.metric}
                            </div>
                            <div className="text-xs text-gray-600">
                              {result.label}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Action Buttons */}
                  <div className="mt-auto pt-4 border-t border-gray-100">
                    <div className="flex flex-col gap-3">
                      <Link 
                        to={`/system/${system.id}`}
                        className="inline-flex items-center justify-center px-6 py-3 rounded-xl font-semibold text-white transition-all duration-300 hover:shadow-lg hover:scale-[1.02] group/btn"
                        style={{ 
                          background: `linear-gradient(135deg, ${system.color} 0%, ${system.color}dd 100%)`,
                        }}
                      >
                        View Details 
                        <FaChevronRight className="ml-2 group-hover/btn:translate-x-1 transition-transform duration-300" />
                      </Link>
                      
                      <a
                        href={`https://wa.me/254762272152?text=${getWhatsAppMessage(system)}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center justify-center px-6 py-3 rounded-xl bg-green-500 text-white font-semibold hover:bg-green-600 transition-all duration-300 hover:shadow-lg"
                      >
                        <FaWhatsapp className="mr-2" />
                        Get Quote
                      </a>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Mini CTA After Grid */}
          <motion.div 
            className="mt-16 bg-gradient-to-r from-[#010536] to-[#020a5c] rounded-3xl p-8 sm:p-12 text-center border border-[#fdaa01]/20"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <FaLightbulb className="text-4xl text-[#fdaa01] mx-auto mb-4 animate-pulse" />
            <h4 className="text-2xl font-bold text-white mb-3">Don't see what you need?</h4>
            <p className="text-white/70 max-w-2xl mx-auto mb-8">
              We can customize any system or build a completely new solution for your specific requirements.
            </p>
            <a
              href="#contact-section"
              className="inline-flex items-center px-8 py-4 rounded-xl bg-gradient-to-r from-[#fdaa01] to-[#ffc042] text-[#010536] font-semibold shadow-lg shadow-[#fdaa01]/30 hover:shadow-xl hover:shadow-[#fdaa01]/40 hover:scale-105 transition-all duration-300"
            >
              Request Custom Solution
              <FaArrowRight className="ml-2" />
            </a>
          </motion.div>
        </div>
      </section>

      {/* Trust Indicators */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center"
          >
            <span className="inline-flex items-center gap-2 bg-[#010536]/5 border border-[#010536]/10 text-[#010536] px-6 py-3 rounded-full text-sm font-semibold mb-6">
              <FaUsers className="text-[#fdaa01]" />
              Trusted by Businesses
            </span>
            
            <h3 className="text-3xl sm:text-4xl font-extrabold text-[#010536] mb-4">
              Trusted by Kenyan Businesses
            </h3>
            <p className="text-gray-600 max-w-2xl mx-auto mb-12">
              Join 100+ satisfied clients who transformed their operations with our systems
            </p>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {[
                { icon: <FaStar />, value: '4.9/5', label: 'Client Rating' },
                { icon: <FaUsers />, value: '100+', label: 'Happy Clients' },
                { icon: <FaCalendarCheck />, value: '98%', label: 'On-Time Delivery' },
                { icon: <FaHeadset />, value: '24/7', label: 'Support Available' }
              ].map((stat, idx) => (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.1 }}
                  whileHover={{ y: -5 }}
                  className="bg-gray-50 rounded-2xl p-8 hover:bg-white hover:shadow-xl transition-all duration-300 border border-gray-100 hover:border-[#fdaa01]/50"
                >
                  <div className="text-3xl text-[#fdaa01] mb-3 flex justify-center">
                    {stat.icon}
                  </div>
                  <div className="text-3xl font-extrabold text-[#010536] mb-2">
                    {stat.value}
                  </div>
                  <div className="text-sm text-gray-600">
                    {stat.label}
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Enhanced Contact CTA Section */}
      <section className="py-20 bg-gray-50" id="contact-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Contact Content */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <span className="inline-flex items-center gap-2 bg-[#fdaa01]/10 border border-[#fdaa01]/20 text-[#fdaa01] px-6 py-3 rounded-full text-sm font-semibold mb-6">
                <FaHeadset />
                Get Started Today
              </span>
              
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-[#010536] mb-6">
                Let's Transform Your{' '}
                <span className="bg-gradient-to-r from-[#fdaa01] to-[#ffc042] bg-clip-text text-transparent">
                  Business
                </span>
              </h2>
              
              <p className="text-lg text-gray-600 leading-relaxed mb-10">
                Schedule a free consultation with our experts. We'll analyze your needs and 
                recommend the perfect solution for your business growth.
              </p>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <a href="tel:+254762272152" className="group flex items-start gap-4 p-4 rounded-xl hover:bg-white hover:shadow-lg transition-all duration-300">
                  <div className="w-12 h-12 rounded-xl bg-[#010536] flex items-center justify-center text-[#fdaa01] flex-shrink-0 group-hover:scale-110 transition-transform duration-300">
                    <FaPhone />
                  </div>
                  <div>
                    <h5 className="font-bold text-[#010536] mb-1">Call Us</h5>
                    <span className="text-gray-600 group-hover:text-[#fdaa01] transition-colors duration-300">
                      +254 762 272 152
                    </span>
                  </div>
                </a>
                
                <a href="https://wa.me/254762272152" target="_blank" rel="noopener noreferrer" className="group flex items-start gap-4 p-4 rounded-xl hover:bg-white hover:shadow-lg transition-all duration-300">
                  <div className="w-12 h-12 rounded-xl bg-green-500 flex items-center justify-center text-white flex-shrink-0 group-hover:scale-110 transition-transform duration-300">
                    <FaWhatsapp />
                  </div>
                  <div>
                    <h5 className="font-bold text-[#010536] mb-1">WhatsApp</h5>
                    <span className="text-gray-600 group-hover:text-green-500 transition-colors duration-300">
                      Chat Now
                    </span>
                  </div>
                </a>
                
                <a href="mailto:shyeecodes@gmail.com" className="group flex items-start gap-4 p-4 rounded-xl hover:bg-white hover:shadow-lg transition-all duration-300">
                  <div className="w-12 h-12 rounded-xl bg-blue-500 flex items-center justify-center text-white flex-shrink-0 group-hover:scale-110 transition-transform duration-300">
                    <FaEnvelope />
                  </div>
                  <div>
                    <h5 className="font-bold text-[#010536] mb-1">Email</h5>
                    <span className="text-gray-600 group-hover:text-blue-500 transition-colors duration-300">
                      shyeecodes@gmail.com
                    </span>
                  </div>
                </a>
                
                <a href="https://calendar.app.google/Ti8ismLSofY2gGci8" target="_blank" rel="noopener noreferrer" className="group flex items-start gap-4 p-4 rounded-xl hover:bg-white hover:shadow-lg transition-all duration-300">
                  <div className="w-12 h-12 rounded-xl bg-[#fdaa01] flex items-center justify-center text-[#010536] flex-shrink-0 group-hover:scale-110 transition-transform duration-300">
                    <FaCalendarCheck />
                  </div>
                  <div>
                    <h5 className="font-bold text-[#010536] mb-1">Schedule</h5>
                    <span className="text-gray-600 group-hover:text-[#fdaa01] transition-colors duration-300">
                      Book Meeting
                    </span>
                  </div>
                </a>
              </div>
            </motion.div>
            
            {/* Contact CTA Card */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="bg-gradient-to-br from-[#010536] to-[#020a5c] rounded-3xl p-8 sm:p-12 text-center shadow-2xl border border-[#fdaa01]/10"
            >
              <div className="text-6xl mb-6">🚀</div>
              <h3 className="text-2xl font-bold text-white mb-4">
                Ready to Get Started?
              </h3>
              <p className="text-white/70 mb-8">
                Book a free strategy call and discover how our ready-to-launch systems can transform your business operations.
              </p>
              <a
                href="https://calendar.app.google/Ti8ismLSofY2gGci8"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center px-8 py-4 rounded-xl bg-gradient-to-r from-[#fdaa01] to-[#ffc042] text-[#010536] font-semibold shadow-lg shadow-[#fdaa01]/30 hover:shadow-xl hover:shadow-[#fdaa01]/40 hover:scale-105 transition-all duration-300"
              >
                <FaCalendarCheck className="mr-2" />
                Book Free Strategy Call
              </a>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Scroll to Top */}
      <motion.button
        initial={{ opacity: 0, scale: 0 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 1 }}
        onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
        className="fixed bottom-6 right-6 z-50 w-14 h-14 rounded-full bg-[#fdaa01] text-[#010536] shadow-lg hover:shadow-xl hover:scale-110 transition-all duration-300 flex items-center justify-center"
      >
        <FaArrowUp />
      </motion.button>
    </div>
  );
};

export default ReadySystems;